import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { loadCommonFoundations } from '../../services/dataApi';

// A language-introduction resource shared identically across every
// curriculum (lives under data-common, not per-target-home skill-path data),
// so it's reachable before a target curriculum is even chosen (deep-linkable
// via ?nativeLanguage=).
function useCommonFoundationsData(langCode) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [usedLang, setUsedLang] = useState(langCode);
  const [error, setError]       = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    loadCommonFoundations(langCode)
      .then(({ data: d, usedLang: lang }) => {
        if (!active) return;
        setData(d);
        setUsedLang(lang);
        setLoading(false);
      })
      .catch(() => {
        if (active) { setError(true); setLoading(false); }
      });
    return () => { active = false; };
  }, [langCode]);

  return { data, loading, usedLang, error };
}

// Section content renders in row order (container-row) — each entry in
// data.sections is a self-contained block; the section's own `type` picks
// which of these generic layouts renders it, so new section kinds (or a
// different content family entirely) just need a new case here rather than
// bespoke top-level fields on the page.
const BULLET_GROUP_STYLES = {
  vowels: { border: 'border-emerald-200', bg: 'bg-emerald-50', title: 'text-emerald-800', text: 'text-emerald-900' },
  consonants: { border: 'border-amber-200', bg: 'bg-amber-50', title: 'text-amber-800', text: 'text-amber-900' },
};
const DEFAULT_BULLET_GROUP_STYLE = { border: 'border-slate-200', bg: 'bg-slate-50', title: 'text-slate-800', text: 'text-slate-700' };

function StageListSection({ section }) {
  return (
    <section className="rounded-2xl bg-white p-6 shadow-sm">
      <h2 className="text-2xl font-semibold text-slate-800">{section.title}</h2>
      <div className="mt-5 space-y-4">
        {(section.items || []).map((item, i) => (
          <article key={i} className="rounded-xl border border-slate-200 overflow-hidden">
            {item.image && (
              <img src={item.image} alt={item.title} className="w-full object-contain" />
            )}
            <div className="p-4">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-lg font-semibold text-slate-800">{item.title}</h3>
                <span className="text-sm font-medium text-blue-700">{item.meta}</span>
              </div>
              <ul className="mt-3 list-disc space-y-1 pl-5 text-slate-700">
                {(item.points || []).map((point, pi) => <li key={pi}>{point}</li>)}
              </ul>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function HeadingSection({ section }) {
  return (
    <section className="rounded-2xl bg-white p-6 shadow-sm">
      <h2 className="text-2xl font-semibold text-slate-800">{section.title}</h2>
      {section.description && <p className="mt-3 text-slate-700">{section.description}</p>}
    </section>
  );
}

function BulletGroupSection({ section }) {
  const style = BULLET_GROUP_STYLES[section.id] || DEFAULT_BULLET_GROUP_STYLE;
  return (
    <div className={`rounded-2xl border ${style.border} ${style.bg} p-6 shadow-sm`}>
      <h3 className={`text-lg font-semibold ${style.title}`}>{section.title}</h3>
      <ul className={`mt-2 list-disc space-y-1 pl-5 ${style.text}`}>
        {(section.points || []).map((p, i) => <li key={i}>{p}</li>)}
      </ul>
    </div>
  );
}

function LetterTableSection({ section }) {
  return (
    <article className="rounded-2xl bg-white p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-slate-800">{section.title}</h3>
      <ul className="mt-3 space-y-2 text-slate-700">
        {(section.items || []).map((item, i) => (
          <li key={i}>
            <span className="font-semibold">{item.letter}</span>
            {item.sound ? ` — ${item.sound}` : ''} — {item.note}
          </li>
        ))}
      </ul>
    </article>
  );
}

function Section({ section }) {
  switch (section.type) {
    case 'stage-list': return <StageListSection section={section} />;
    case 'heading': return <HeadingSection section={section} />;
    case 'bullet-group': return <BulletGroupSection section={section} />;
    case 'letter-table': return <LetterTableSection section={section} />;
    default: return null;
  }
}

function Skeleton() {
  return (
    <div className="space-y-8 animate-pulse">
      {[1, 2, 3].map((i) => (
        <div key={i} className="rounded-2xl bg-white p-6 shadow-sm space-y-3">
          <div className="h-6 w-1/3 rounded bg-slate-200" />
          <div className="h-4 w-full rounded bg-slate-100" />
          <div className="h-4 w-5/6 rounded bg-slate-100" />
        </div>
      ))}
    </div>
  );
}

export default function CommonFoundationsPage() {
  const { t } = useTranslation();
  const { languagePrefs } = useAuth();
  const [searchParams] = useSearchParams();
  const requestedLanguage = searchParams.get('nativeLanguage');
  const deepLinkLanguage = requestedLanguage && /^[a-z]{2,3}$/i.test(requestedLanguage)
    ? requestedLanguage.toLowerCase()
    : null;
  const langCode = deepLinkLanguage || languagePrefs?.nativeLanguage || 'en';
  const { data, loading, usedLang, error } = useCommonFoundationsData(langCode);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 px-4 py-8">
      <div className="mx-auto max-w-5xl space-y-8">

        {/* Header */}
        <section className="rounded-2xl bg-white p-6 shadow-sm">
          {error ? (
            <p className="text-slate-600">{t('commonFoundations.loadError')}</p>
          ) : loading || !data ? (
            <div className="animate-pulse space-y-3">
              <div className="h-8 w-2/3 rounded bg-slate-200" />
              <div className="h-4 w-full rounded bg-slate-100" />
            </div>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-slate-800">{data.title}</h1>
              <p className="mt-3 text-slate-600">{data.subtitle}</p>
              {usedLang !== langCode && (
                <p className="mt-2 text-xs font-semibold text-amber-600">
                  {t('commonFoundations.fallbackNotice')}
                </p>
              )}
              <Link
                to="/learn/common-foundations/lesson"
                className="mt-5 inline-flex items-center gap-2 rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-violet-700"
              >
                ▶ {t('journeyPages.startInteractiveLesson')}
              </Link>
            </>
          )}
        </section>

        {!error && (loading || !data ? <Skeleton /> : (
          <>
            {(data.sections || []).map((section) => (
              <Section key={section.id} section={section} />
            ))}
          </>
        ))}
      </div>
    </div>
  );
}
